/**
 * Router Configuration
 * Defines all application routes with public/protected guards
 * Following Clean Architecture: Router is part of infrastructure layer
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute, PublicRoute } from '@/components/routing';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoginPage, DashboardRedirect, ComingSoon } from '@/pages';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import {
  PipelineMatrixPage,
  CreditsListPage,
  CreditDetailPage,
  ApplicationsListPage,
  ApplicationDetailPage,
  OffersListPage,
  OfferDetailPage,
  FinancialProfilePage,
} from '@/features/credit';
import { PoliciesPage, ConfigHistoryPage, SplitPage } from '@/features/policies';
import { SimulatorPage } from '@/features/simulator';
import {
  CarteraPage,
  CobrosPage,
  CobradosPage,
  CollectionDayPage,
  DelinquencyPage,
  LatentCollectionPage,
  LiensListPage,
  LienDetailPage,
  CastigadosPage,
} from '@/features/collection';
import { TransfersPage } from '@/features/transfers';
import {
  PortfolioHistoricoPage,
  PortafolioPage,
} from '@/features/portfolio';
import {
  FundingInstructionsPage,
  FundingDetailPage,
  FundingHistoryPage,
  TransferHistoryPage,
  FondeoPage,
  ConfirmarFondeoPage,
} from '@/features/funding';
import { TransferenciasPage } from '@/features/transfers/pages/TransferenciasPage';
import { ConfirmarTransferenciaPage } from '@/features/transfers/pages/ConfirmarTransferenciaPage';
import { CierreMensualPage } from '@/features/accounting/pages/CierreMensualPage';
import { NurturingPage } from '@/features/nurturing';
import { OriginationPage } from '@/features/origination';
import { ScoringPage, CandidatosPage } from '@/features/scoring';
import { ReconciliationPage, CollectorReconciliationPage, FSAReconciliationPage, PCFReconciliationPage } from '@/features/reconciliation';
import { JobsPage } from '@/features/jobs';
import { AlertsPage } from '@/features/alerts';
import { DefinicionesPage } from '@/features/operations';
import { ExpedientesSearchPage, ExpedienteDetailPage } from '@/features/expedientes';
import { ClientsListPage } from '@/features/clients';
import { SICReportPage, SICReportDetailPage } from '@/features/sic-report';
import { ROUTES } from '@/constants';

export const router = createBrowserRouter([
  // ─── Public routes ─────────────────────────────────────────────────────────
  {
    element: <PublicRoute />,
    children: [
      {
        path: ROUTES.LOGIN,
        element: <LoginPage />,
      },
      { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
    ],
  },

  // ─── Protected routes with AppLayout ───────────────────────────────────────
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          // Dashboard redirect (role-based)
          {
            path: ROUTES.DASHBOARD,
            element: <DashboardRedirect />,
          },

          // ── Credit ────────────────────────────────────────────────────────
          {
            path: ROUTES.CREDIT_DASHBOARD,
            element: <PipelineMatrixPage />,
          },
          {
            path: ROUTES.PIPELINE_MATRIX,
            element: <PipelineMatrixPage />,
          },
          {
            path: ROUTES.ACTIVE_CREDITS,
            element: <CreditsListPage />,
          },
          {
            path: ROUTES.CREDIT_DETAIL,
            element: <CreditDetailPage />,
          },
          {
            path: ROUTES.NURTURING,
            element: <NurturingPage />,
          },
          {
            path: ROUTES.NURTURING_DETAIL,
            element: <NurturingPage />,
          },
          {
            path: ROUTES.ORIGINATION,
            element: <OriginationPage />,
          },
          {
            path: ROUTES.APPLICATIONS_LIST,
            element: <ApplicationsListPage />,
          },
          {
            path: ROUTES.APPLICATION_DETAIL,
            element: <ApplicationDetailPage />,
          },
          {
            path: ROUTES.OFFERS_LIST,
            element: <OffersListPage />,
          },
          {
            path: ROUTES.OFFER_DETAIL,
            element: <OfferDetailPage />,
          },
          {
            path: ROUTES.FINANCIAL_PROFILE,
            element: <FinancialProfilePage />,
          },
          {
            path: ROUTES.SCORING,
            element: <ScoringPage />,
          },
          {
            path: ROUTES.CREDIT_CANDIDATOS,
            element: <CandidatosPage />,
          },

          // ── Collection ────────────────────────────────────────────────────
          {
            path: ROUTES.COLLECTION_DASHBOARD,
            element: <CobrosPage />,
          },
          {
            // Ruta legacy — Agendados se fusionó dentro de Cobranza
            path: ROUTES.COLLECTION_AGENDADOS,
            element: <Navigate to={ROUTES.COLLECTION_DASHBOARD} replace />,
          },
          {
            path: ROUTES.COLLECTION_CASTIGADOS,
            element: <CastigadosPage />,
          },
          {
            path: ROUTES.COLLECTION_COBRADOS,
            element: <CobradosPage />,
          },
          {
            path: ROUTES.COLLECTION_CARTERA,
            element: <CarteraPage />,
          },
          {
            path: ROUTES.COLLECTION_DAY,
            element: <CollectionDayPage />,
          },
          {
            path: ROUTES.DELINQUENCY,
            element: <DelinquencyPage />,
          },
          {
            path: ROUTES.LATENT_COLLECTION,
            element: <LatentCollectionPage />,
          },
          {
            path: ROUTES.LIENS_LIST,
            element: <LiensListPage />,
          },
          {
            path: ROUTES.LIEN_DETAIL,
            element: <LienDetailPage />,
          },
          {
            path: ROUTES.COLLECTION_TRANSFERS,
            element: <TransfersPage />,
          },

          // ── Portfolio ─────────────────────────────────────────────────────
          {
            path: ROUTES.PORTFOLIO_DASHBOARD,
            element: <PortafolioPage />,
          },
          {
            path: ROUTES.PORTFOLIO_HISTORICO,
            element: <PortfolioHistoricoPage />,
          },

          // ── Funding ───────────────────────────────────────────────────────
          {
            path: ROUTES.FONDEO,
            element: <FondeoPage />,
          },
          {
            path: ROUTES.CONFIRMAR_FONDEO,
            element: <ConfirmarFondeoPage />,
          },
          {
            path: ROUTES.TRANSFERENCIAS,
            element: <TransferenciasPage />,
          },
          {
            path: ROUTES.CONFIRMAR_TRANSFERENCIA,
            element: <ConfirmarTransferenciaPage />,
          },
          {
            path: ROUTES.CIERRE_MENSUAL,
            element: <CierreMensualPage />,
          },
          {
            path: ROUTES.FUNDING_INSTRUCTIONS,
            element: <FundingInstructionsPage />,
          },
          {
            path: ROUTES.FUNDING_INSTRUCTION_DETAIL,
            element: <FundingDetailPage />,
          },
          {
            path: ROUTES.FUNDING_HISTORY,
            element: <FundingHistoryPage />,
          },
          {
            path: ROUTES.TRANSFER_HISTORY,
            element: <TransferHistoryPage />,
          },

          // ── Policies & Tools ──────────────────────────────────────────────
          {
            path: ROUTES.POLICIES,
            element: <PoliciesPage />,
          },
          {
            path: ROUTES.CONFIG_HISTORY,
            element: <ConfigHistoryPage />,
          },
          {
            path: ROUTES.CREDIT_SIMULATOR,
            element: <SimulatorPage />,
          },

          // ── Operations (FiadoSA) ──────────────────────────────────────────
          {
            path: ROUTES.OPS_DEFINITIONS,
            element: <DefinicionesPage />,
          },
          {
            path: ROUTES.RECONCILIATION_DASHBOARD,
            element: <ReconciliationPage />,
          },
          {
            path: ROUTES.RECONCILIATION_COLLECTOR,
            element: <CollectorReconciliationPage />,
          },
          {
            path: ROUTES.RECONCILIATION_FSA,
            element: <FSAReconciliationPage />,
          },
          {
            path: ROUTES.RECONCILIATION_PCF,
            element: <PCFReconciliationPage />,
          },
          {
            path: ROUTES.JOBS_CALENDAR,
            element: <JobsPage />,
          },
          {
            path: ROUTES.ALERTS,
            element: <AlertsPage />,
          },
          {
            path: ROUTES.SPLIT_CONFIG,
            element: <SplitPage />,
          },

          // ── Operations (UNE-Credito) ─────────────────────────────────────────
          {
            path: ROUTES.SIC_REPORT,
            element: <SICReportPage />,
          },
          {
            path: ROUTES.SIC_REPORT_DETAIL,
            element: <SICReportDetailPage />,
          },

          // ── Clientes (P10) ─────────────────────────────────────────────────
          {
            path: ROUTES.CLIENTS_LIST,
            element: <ClientsListPage />,
          },
          {
            path: ROUTES.EXPEDIENTES_SEARCH,
            element: <ExpedientesSearchPage />,
          },
          {
            path: ROUTES.EXPEDIENTE_DETAIL,
            element: <ExpedienteDetailPage />,
          },

          // ── Profile / Settings ────────────────────────────────────────────
          {
            path: ROUTES.PROFILE,
            element: <ComingSoon />,
          },
          {
            path: ROUTES.SETTINGS,
            element: <ComingSoon />,
          },
        ],
      },
    ],
  },

  // ─── Redirects ─────────────────────────────────────────────────────────────
  {
    path: ROUTES.HOME,
    element: <Navigate to={ROUTES.DASHBOARD} replace />,
  },
  {
    path: '*',
    element: <Navigate to={ROUTES.DASHBOARD} replace />,
  },
]);
